<template>
  <v-container fluid class="px-lg-5">
    <app-header
      :title="pageTitle"
      :breadcrumbs="breadcrumbs"
      :showButton="false"
    />
    <v-form ref="form" v-model="valid" lazy-validation>
      <v-card class="rounded-card mb-4" flat outlined>
        <v-card-text>
          <app-field type="text" :label="$t('views.appraisals.formTitle')" :disabled="isView"
            v-model="formTitle" :rules="requiredRule" />
        </v-card-text>
      </v-card>

      <v-card v-for="(question, index) in questions" :key="question.key" class="rounded-card mb-4" flat outlined>
        <v-card-text>
          <v-row align="start" no-gutters>
            <v-col class="col-12 py-0" :class="isView ? 'col-md-12' : 'col-md-7'">
              <app-field type="text" :label="$t('views.appraisals.questionText')" hide-details="auto"
                :disabled="isView" v-model="question.text" :rules="requiredRule" />
            </v-col>
            <v-col v-if="!isView" class="col-9 col-md-4 py-0 pl-md-3">
              <app-field type="select" :label="$t('views.appraisals.questionType')" hide-details="auto"
                v-model="question.type" :options="questionTypeOptions" @selection-changed="onTypeChanged(question)"
                item-text="text" item-value="value" :rules="requiredSelectRule" />
            </v-col>
            <v-col v-if="isView" class="col-12 col-md-6 py-0 mt-3">
              <app-field type="text" :label="$t('views.appraisals.questionType')" hide-details="auto"
                disabled :value="questionTypeLabel(question.type)" />
            </v-col>
            <v-col v-if="!isView" class="col-3 col-md-1 py-0 d-flex align-center justify-end field-height">
              <v-btn icon @click="removeQuestion(index)" :disabled="questions.length === 1">
                <v-icon>mdi-trash-can-outline</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <template v-if="isChoiceType(question.type)">
            <app-field type="checkbox" :label="$t('views.appraisals.isScored')" hide-details="auto"
              :disabled="isView" v-model="question.isScored" @input="onScoredChanged(question)" />

            <v-row v-if="question.isScored" no-gutters class="mt-2 mb-2">
              <v-col class="col-12 col-md-4">
                <app-field type="number" :label="$t('views.appraisals.weight')" hide-details="auto"
                  :disabled="isView" v-model="question.weight" :rules="requiredRule" />
              </v-col>
            </v-row>

            <v-row v-for="(option, optionIndex) in question.options" :key="option.key" align="start" no-gutters
              class="mt-3">
              <v-col class="col-auto pr-2 d-flex align-center field-height">
                <v-icon size="20">{{ optionIcon(question.type) }}</v-icon>
              </v-col>
              <v-col class="col">
                <app-field type="text" :label="$t('views.appraisals.optionText', {number: optionIndex + 1})"
                  hide-details="auto" :disabled="isView" v-model="option.text" :rules="requiredRule" />
              </v-col>
              <v-col v-if="question.isScored" class="col-3 col-md-2 pl-2">
                <app-field type="number" :label="$t('views.appraisals.optionScore')" hide-details="auto"
                  :disabled="isView" v-model="option.score" />
              </v-col>
              <v-col v-if="!isView" class="col-auto d-flex align-center field-height">
                <v-btn icon small @click="removeOption(question, optionIndex)"
                  :disabled="question.options.length === minOptionsCount">
                  <v-icon size="20">mdi-close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-btn v-if="!isView" text color="primary" small class="mt-2" @click="addOption(question)">
              <v-icon left small>mdi-plus</v-icon>{{ $t('views.appraisals.addOption') }}
            </v-btn>
          </template>
        </v-card-text>
      </v-card>

      <v-btn v-if="!isView" outlined color="primary" class="mb-6" @click="addQuestion">
        <v-icon left>mdi-plus</v-icon>{{ $t('views.appraisals.addQuestion') }}
      </v-btn>

      <v-row no-gutters>
        <v-col class="col-12 text-right">
          <v-btn v-if="!isView" class="primary mr-2" :disabled="!valid" @click="handleSubmit">
            {{ isEdit ? $t('actions.update') : $t('actions.create', {entity: $tc('models.appraisalForm.entity', 1)}) }}
          </v-btn>
          <v-btn class="secondary" @click="handleCancel">
            {{ isView ? $t('actions.back') : $t('actions.cancel') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import {appraisalQuestionTypes, appraisalTemplateQuestionTypes} from 'constants';

export default {
  data() {
    const hasId = !!this.$route.params.id;
    const isView = this.$route.name === 'ViewAppraisalForm';
    const isEdit = hasId && !isView;
    const titleKey = isView ? 'actions.view' : (isEdit ? 'actions.edit' : 'layout.mainMenu.createForm');
    return {
      valid: true,
      hasId,
      isView,
      isEdit,
      nextQuestionKey: 1,
      nextOptionKey: 1,
      minOptionsCount: 2,
      removedQuestions: [],
      pageTitle: {
        text: this.$t(titleKey),
        icon: 'mdi-clipboard-text-outline'
      },
      breadcrumbs: [
        {
          text: this.$t('layout.mainMenu.home'),
          to: {name: 'Home'}
        },
        {
          text: this.$t('layout.mainMenu.appraisals'),
          disabled: true
        },
        {
          text: this.$t(titleKey),
          to: {
            name: isView ? 'ViewAppraisalForm' : (isEdit ? 'EditAppraisalForm' : 'CreateAppraisalForm'),
            params: hasId ? {id: this.$route.params.id} : undefined
          },
          exact: true
        }
      ],
      requiredRule: [{name: 'required'}],
      requiredSelectRule: [{name: 'requiredSelect'}],
      formTitle: '',
      questions: [],
      questionTypeOptions: [
        {
          text: this.$t('views.appraisals.questionTypes.multipleChoice'),
          value: appraisalQuestionTypes.MULTIPLE_CHOICE
        },
        {
          text: this.$t('views.appraisals.questionTypes.textArea'),
          value: appraisalQuestionTypes.TEXT_AREA
        }
      ]
    };
  },
  computed: {
    ...mapGetters(['appraisalTemplate'])
  },
  methods: {
    ...mapActions(['createAppraisalTemplate', 'updateAppraisalTemplate', 'getAppraisalTemplate']),
    buildOption(existing = {}) {
      const attributes = existing.attributes || {};
      const option = {
        id: existing.id || null,
        key: this.nextOptionKey,
        text: attributes.body || '',
        score: attributes.score != null ? attributes.score : null
      };
      this.nextOptionKey++;
      return option;
    },
    buildQuestion(existing = {}) {
      const attributes = existing.attributes || {};
      const isChoice = attributes.question_type === appraisalTemplateQuestionTypes.MCQ;
      const question = {
        id: existing.id || null,
        key: this.nextQuestionKey,
        text: attributes.body || '',
        type: attributes.question_type ?
          (isChoice ? appraisalQuestionTypes.MULTIPLE_CHOICE : appraisalQuestionTypes.TEXT_AREA) :
          '',
        options: (attributes.options || []).map((option) => this.buildOption(option)),
        isScored: attributes.is_scored || false,
        weight: attributes.weight != null ? attributes.weight : null,
        removedOptions: []
      };
      this.nextQuestionKey++;
      return question;
    },
    isChoiceType(type) {
      return type === appraisalQuestionTypes.MULTIPLE_CHOICE || type === appraisalQuestionTypes.CHECKBOX;
    },
    clearOptions(question) {
      question.options.forEach((option) => {
        if (option.id) {
          question.removedOptions.push({id: option.id, _destroy: true});
        }
      });
      question.options = [];
    },
    onTypeChanged(question) {
      if (this.isChoiceType(question.type)) {
        while (question.options.length < this.minOptionsCount) {
          question.options.push(this.buildOption());
        }
      } else {
        this.clearOptions(question);
        question.isScored = false;
        question.weight = null;
      }
    },
    onScoredChanged(question) {
      if (!question.isScored) {
        question.weight = null;
        question.options.forEach((option) => (option.score = null));
      }
    },
    optionIcon(type) {
      return type === appraisalQuestionTypes.CHECKBOX ? 'mdi-checkbox-blank-outline' : 'mdi-radiobox-blank';
    },
    questionTypeLabel(type) {
      const option = this.questionTypeOptions.find((option) => option.value === type);
      return option ? option.text : '';
    },
    addQuestion() {
      this.questions.push(this.buildQuestion());
    },
    removeQuestion(index) {
      const [question] = this.questions.splice(index, 1);
      if (question.id) {
        this.removedQuestions.push({id: question.id, _destroy: true});
      }
    },
    addOption(question) {
      question.options.push(this.buildOption());
    },
    removeOption(question, index) {
      const [option] = question.options.splice(index, 1);
      if (option.id) {
        question.removedOptions.push({id: option.id, _destroy: true});
      }
    },
    buildQuestionPayload(question, index) {
      const isScored = this.isChoiceType(question.type) && question.isScored;
      const payload = {
        body: question.text,
        question_type: this.isChoiceType(question.type) ?
          appraisalTemplateQuestionTypes.MCQ :
          appraisalTemplateQuestionTypes.FREE_TEXT,
        position: index + 1,
        is_scored: isScored,
        weight: isScored ? question.weight : null
      };
      if (question.id) {
        payload.id = question.id;
      }
      if (this.isChoiceType(question.type)) {
        const options = question.options.map((option, optionIndex) => {
          const optionPayload = {
            body: option.text, position: optionIndex + 1, score: isScored ? option.score : null
          };
          if (option.id) {
            optionPayload.id = option.id;
          }
          return optionPayload;
        });
        payload.appraisal_template_question_options_attributes = [...options, ...question.removedOptions];
      } else if (question.removedOptions.length) {
        payload.appraisal_template_question_options_attributes = [...question.removedOptions];
      }
      return payload;
    },
    buildPayload() {
      const questionPayloads = this.questions.map((question, index) => this.buildQuestionPayload(question, index));
      return {
        name: this.formTitle,
        appraisal_template_questions_attributes: [...questionPayloads, ...this.removedQuestions]
      };
    },
    async handleSubmit() {
      if (!this.$refs.form.validate()) {
        return;
      }
      const response = this.isEdit ?
        await this.updateAppraisalTemplate({id: this.$route.params.id, data: this.buildPayload()}) :
        await this.createAppraisalTemplate(this.buildPayload());
      if (!this.successCode(response.status)) {
        this.displayServerErrorsInToaster(response.errors);
      }
    },
    handleCancel() {
      this.$router.push({name: this.hasId ? 'AppraisalTemplates' : 'Home'});
    }
  },
  async created() {
    if (this.hasId) {
      await this.getAppraisalTemplate(this.$route.params.id);
      if (this.appraisalTemplate) {
        this.formTitle = this.appraisalTemplate.name;
        this.questions = this.appraisalTemplate.questions.map((question) => this.buildQuestion(question));
      }
    }
    if (!this.questions.length) {
      this.questions.push(this.buildQuestion());
    }
  }
};
</script>

<style scoped>
.field-height {
  min-height: 40px;
}
</style>
